import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { SharedDatasetManagerDatasetSchemaEncoderSchemaViolation as SchemaViolation } from '@rossum/mdh-api-client';
import { DatasetColumn, isDeclaredColumnType } from './types';

// Rows named per problem; more are only hinted at.
const MAX_SHOWN_ROWS = 3;

const formatRows = (violations: SchemaViolation[]) => {
  const rows = R.unique(violations.map(({ row }) => row));
  const shown = rows.slice(0, MAX_SHOWN_ROWS).join(', ');
  return {
    count: rows.length,
    rows: rows.length > MAX_SHOWN_ROWS ? `${shown}…` : shown,
  };
};

const formatProblem = (
  intl: IntlShape,
  violations: [SchemaViolation, ...SchemaViolation[]],
  columns: DatasetColumn[]
) => {
  const [violation] = violations;
  const column = columns.find(
    ({ datasetAttributeName }) => datasetAttributeName === violation.field
  );
  const values = {
    column: column?.name ?? violation.field,
    ...formatRows(violations),
  };

  switch (violation.code) {
    case 'unknown_field':
      return intl.formatMessage(
        { id: 'features.datasets.violation.unknownField' },
        values
      );
    case 'missing_field':
      return intl.formatMessage(
        { id: 'features.datasets.violation.missingField' },
        values
      );
    case 'type_mismatch':
      // Other types can't be declared, so MDH's own words fit them better.
      return column && isDeclaredColumnType(column.type)
        ? intl.formatMessage(
            { id: 'features.datasets.violation.typeMismatch' },
            { ...values, type: column.type }
          )
        : violation.message;
    // A code MDH adds later.
    default:
      return violation.message;
  }
};

// MDH reports every bad cell in the words of a schema validator; this makes
// one problem per column and kind, naming the column the way the user sees it.
export const formatViolations = (
  intl: IntlShape,
  violations: SchemaViolation[],
  columns: DatasetColumn[]
) =>
  R.pipe(
    violations,
    R.groupBy(({ code, field }) => `${code}:${field}`),
    R.values(),
    R.map(group => formatProblem(intl, group, columns))
  );
